/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Trash2, X } from 'lucide-react';
import { CompanyService } from '../../services/company.service';
import { toast } from 'react-hot-toast';

const allFields = [
  { key: 'programNumber', label: 'Program Number' },
  { key: 'UMR', label: 'UMR' },
  { key: 'mainLineOfBusiness', label: 'Main Line of Business' },
  { key: 'subLineOfBusiness', label: 'Sub Line of Business' },
  { key: 'typeOfTreaty', label: 'Type of Treaty' },
  { key: 'businessTitle', label: 'Business Title' },
  { key: 'underwritingYear', label: 'Underwriting Year' },
  { key: 'inceptionDate', label: 'Inception Date' },
  { key: 'expiryDate', label: 'Expiry Date' },
  { key: 'multiYear', label: 'Multi Year' },
  { key: 'contractDuration', label: 'Contract Duration' },
  { key: 'mainTerritorialScope', label: 'Main Territorial Scope' },
  { key: 'contractCurrency', label: 'Contract Currency' },
  { key: 'claimBasis', label: 'Claim Basis' },
  { key: 'accountingBasis', label: 'Accounting Basis' },
  { key: 'brokerageRate', label: 'Brokerage Rate' },
  { key: 'taxesRate', label: 'Taxes Rate' },
  { key: 'cedant', label: 'Cedant' },
  { key: 'broker', label: 'Broker' },
  { key: 'country', label: 'Country' },
];

export const ProgramDefinitionPage = () => {
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [originalFields, setOriginalFields] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchDefinition = async () => {
      setLoading(true);
      try {
        const res = await CompanyService.getProgramDefinition();
        const data = res.data || [];
        setSelectedFields(data);
        setOriginalFields(data); 
      } catch (e) {
        toast.error('Failed to load program definition.');
      } finally {
        setLoading(false);
      }
    };

    fetchDefinition();
  }, []);

  const availableFields = allFields.filter((f) => !selectedFields.includes(f.key));

  const handleAdd = (key: string) => {
    setSelectedFields((prev) => [...prev, key]);
  };

  const handleRemove = (key: string) => {
    setSelectedFields((prev) => prev.filter((f) => f !== key));
  };

  const handleRemoveAll = () => {
    setSelectedFields([]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await CompanyService.updateProgramDefinition({ fields: selectedFields });
      toast.success('Program definition saved.');
      setOriginalFields(selectedFields); 
    } catch (e) {
      toast.error('Failed to save program definition.');
    } finally {
      setSaving(false);
    }
  };

  const arraysEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, index) => val === sortedB[index]);
  };

  const isUnchanged = arraysEqual(selectedFields, originalFields);

  return (
    <div className="overflow-hidden">
      <h1 className="mb-6 text-4xl font-bold text-center sm:text-left text-primary">
        Program Definition
      </h1>

      <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
        <div className="flex-1">
          <h2 className="mb-2 font-semibold">Selected Fields</h2>
          <div className="min-h-[48px] flex flex-wrap gap-2 p-2 border rounded bg-gray-50">
            {selectedFields.map((key) => {
              const field = allFields.find((f) => f.key === key);
              return (
                <div
                  key={key}
                  className="flex items-center gap-1 px-2 py-1 text-sm bg-blue-100 border border-blue-300 rounded"
                >
                  <span>{field?.label}</span>
                  <button onClick={() => handleRemove(key)}>
                    <X className="w-4 h-4 text-red-600 hover:text-red-800" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-3 mt-4">
            <Button onClick={handleSave} disabled={saving || isUnchanged}>
              {saving ? 'Saving...' : 'Save Definition'}
            </Button>
            {selectedFields.length > 0 && (
              <button
                onClick={handleRemoveAll}
                className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800"
              >
                <Trash2 className="w-4 h-4" />
                Remove All
              </button>
            )}
          </div>
        </div>

        <div className="flex-1">
          <h2 className="mb-2 font-semibold">Available Fields</h2>
          <div className="flex flex-wrap gap-2 max-h-[220px] overflow-y-auto pr-1">
            {availableFields.map((field) => (
              <button
                key={field.key}
                onClick={() => handleAdd(field.key)}
                className="flex items-center gap-1 px-3 py-1 text-sm bg-white border rounded shadow cursor-pointer hover:bg-gray-50"
              >
                {field.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
